
import Vue from "vue";
import TempStarsTableTitle from "@/components/shared/TempStarsTableTitle/TempStarsTableTitle.vue";
import {
  getFeatureFlags,
  getFeatureFlagHistory,
  updateFeatureFlag,
  FeatureFlagItem,
  FeatureFlagMode,
  FeatureFlagHistoryEntry
} from "@/api";

const CATEGORY_TITLES: Record<string, string> = {
  ffs: "FFS / Membership",
  boost: "Boost",
  listings: "Listings",
  platform: "Platform"
};
const CATEGORY_ORDER = ["ffs", "boost", "listings", "platform"];

export default Vue.extend({
  name: "FeatureFlags",
  components: { TempStarsTableTitle },
  data: () => ({
    loading: false,
    savingKey: "" as string,
    error: "",
    search: "",
    environment: "",
    canWrite: true,
    flags: [] as FeatureFlagItem[],
    switchDrafts: {} as Record<string, boolean>,
    modeDrafts: {} as Record<string, string>,
    dateDrafts: {} as Record<string, string>,
    snackbar: { visible: false, text: "", color: "success" },
    confirmDialog: {
      show: false,
      title: "",
      currentLabel: "",
      nextLabel: "",
      impact: "",
      hazardous: false,
      blastStatement: "",
      flipInput: "",
      note: "",
      flag: null as FeatureFlagItem | null,
      payload: null as { enabled?: 0 | 1; value?: string | null } | null,
      resolve: null as null | ((ok: boolean) => void)
    },
    historyDialog: {
      show: false,
      key: "",
      label: "",
      loading: false,
      error: "",
      entries: [] as FeatureFlagHistoryEntry[]
    },
    historyRequestId: 0
  }),
  computed: {
    environmentLabel(): string {
      const env = (this.environment || "").toLowerCase();
      if (env === "production") return "production";
      if (env === "staging") return "staging";
      if (env === "development" || env === "develop") return "development";
      return this.environment || "unknown";
    },
    envChipColor(): string {
      const name = this.environmentLabel;
      if (name === "production") return "#13CA82";
      if (name === "staging") return "#FF9800";
      if (name === "development") return "#2196F3";
      return "#FF9800";
    },
    subtitle(): string {
      const base = `Live state of the ${this.environmentLabel} database · changes take effect immediately`;
      if (this.environmentLabel === "production") {
        return `${base} · flips are emailed to the flag-admin list`;
      }
      return base;
    },
    filteredFlags(): FeatureFlagItem[] {
      const q = (this.search || "").trim().toLowerCase();
      if (!q) return this.flags;
      return this.flags.filter(
        f =>
          f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q)
      );
    },
    groupedFlags(): Array<{
      category: string;
      title: string;
      flags: FeatureFlagItem[];
    }> {
      const groups: Array<{
        category: string;
        title: string;
        flags: FeatureFlagItem[];
      }> = [];
      for (const category of CATEGORY_ORDER) {
        const flags = this.filteredFlags.filter(f => f.category === category);
        if (flags.length) {
          groups.push({
            category,
            title: CATEGORY_TITLES[category] || category,
            flags
          });
        }
      }
      const known = new Set(CATEGORY_ORDER);
      const extras = this.filteredFlags.filter(f => !known.has(f.category));
      if (extras.length) {
        groups.push({ category: "other", title: "Other", flags: extras });
      }
      return groups;
    },
    flipArmed(): boolean {
      return this.confirmDialog.flipInput === "FLIP";
    },
    productionReasonRequired(): boolean {
      return (
        this.confirmDialog.hazardous && this.environmentLabel === "production"
      );
    },
    confirmEnabled(): boolean {
      if (this.confirmDialog.hazardous && !this.flipArmed) return false;
      if (
        this.productionReasonRequired &&
        !(this.confirmDialog.note || "").trim()
      ) {
        return false;
      }
      return true;
    }
  },
  async created() {
    try {
      await this.load();
    } catch (err) {
      if (err && err.status === 403) {
        this.$router.replace({ name: "home" });
        return;
      }
      this.error =
        err instanceof Error ? err.message : "Unable to load feature flags";
    }
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const payload = await getFeatureFlags();
        this.environment = payload.environment;
        this.canWrite = payload.canWrite !== false;
        this.flags = payload.flags || [];
        this.syncControlDrafts();
      } finally {
        this.loading = false;
      }
    },
    canEdit(flag: FeatureFlagItem): boolean {
      if (!this.canWrite) return false;
      if (flag.status === "unregistered") return false;
      if (flag.status === "missing-row" || flag.missingRow) return false;
      if (flag.inert || flag.status === "inert") return false;
      return true;
    },
    editDisabledReason(flag: FeatureFlagItem): string {
      if (flag.status === "unregistered") {
        return "Unregistered flags are display-only.";
      }
      if (flag.status === "missing-row" || flag.missingRow) {
        return "no DB row — fail-closed OFF";
      }
      if (flag.inert || flag.status === "inert") {
        return "This switch isn't connected to anything yet.";
      }
      return "Flipping arrives with the next release.";
    },
    formatWhen(when: string): string {
      if (!when) return "";
      try {
        return new Date(when)
          .toISOString()
          .replace("T", " ")
          .replace(/\.\d+Z$/, " UTC");
      } catch {
        return when;
      }
    },
    formatRelativeWhen(when: string): string {
      if (!when) return "";
      const timestamp = new Date(when).getTime();
      if (!Number.isFinite(timestamp)) return when;

      const elapsedSeconds = Math.round((Date.now() - timestamp) / 1000);
      const future = elapsedSeconds < 0;
      const absoluteSeconds = Math.abs(elapsedSeconds);
      if (absoluteSeconds < 45) return "just now";

      const units = [
        { seconds: 365 * 24 * 60 * 60, label: "year" },
        { seconds: 30 * 24 * 60 * 60, label: "month" },
        { seconds: 7 * 24 * 60 * 60, label: "week" },
        { seconds: 24 * 60 * 60, label: "day" },
        { seconds: 60 * 60, label: "hour" },
        { seconds: 60, label: "minute" }
      ];
      const unit = units.find(
        candidate => absoluteSeconds >= candidate.seconds
      );
      if (!unit) return "just now";
      const amount = Math.max(1, Math.round(absoluteSeconds / unit.seconds));
      const label = amount === 1 ? unit.label : `${unit.label}s`;
      return future ? `in ${amount} ${label}` : `${amount} ${label} ago`;
    },
    switchDraftValue(flag: FeatureFlagItem): boolean {
      if (Object.prototype.hasOwnProperty.call(this.switchDrafts, flag.key)) {
        return this.switchDrafts[flag.key];
      }
      return flag.enabled === 1;
    },
    modeDraftValue(flag: FeatureFlagItem): string {
      if (Object.prototype.hasOwnProperty.call(this.modeDrafts, flag.key)) {
        return this.modeDrafts[flag.key];
      }
      return flag.modeKey || "";
    },
    setControlDraftFromFlag(flag: FeatureFlagItem) {
      if (flag.control === "switch") {
        this.$set(this.switchDrafts, flag.key, flag.enabled === 1);
      } else if (flag.control === "modes") {
        this.$set(this.modeDrafts, flag.key, flag.modeKey || "");
      } else if (flag.control === "date") {
        this.setDateDraft(flag, flag.value);
      }
    },
    dateInputValue(value: string | null): string {
      if (!value) return "";
      const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
      return match ? match[1] : "";
    },
    dateDraftValue(flag: FeatureFlagItem): string {
      if (Object.prototype.hasOwnProperty.call(this.dateDrafts, flag.key)) {
        return this.dateDrafts[flag.key];
      }
      return this.dateInputValue(flag.value);
    },
    setDateDraft(flag: FeatureFlagItem, value: string | null) {
      this.$set(this.dateDrafts, flag.key, this.dateInputValue(value));
    },
    syncControlDrafts() {
      const switches: Record<string, boolean> = {};
      const modes: Record<string, string> = {};
      const dates: Record<string, string> = {};
      this.flags.forEach(flag => {
        if (flag.control === "switch") {
          switches[flag.key] = flag.enabled === 1;
        } else if (flag.control === "modes") {
          modes[flag.key] = flag.modeKey || "";
        } else if (flag.control === "date") {
          dates[flag.key] = this.dateInputValue(flag.value);
        }
      });
      this.switchDrafts = switches;
      this.modeDrafts = modes;
      this.dateDrafts = dates;
    },
    formatState(
      enabled: 0 | 1 | null,
      value: string | null,
      flag: FeatureFlagItem
    ): string {
      if (flag.control === "modes") {
        const mode = (flag.modes || []).find(
          m => m.enabled === enabled && String(m.value) === String(value)
        );
        return mode ? mode.key : `${enabled}/${value}`;
      }
      if (flag.control === "date") {
        return value || "(cleared)";
      }
      return enabled === 1 ? "ON" : "OFF";
    },
    stateLabel(
      flag: FeatureFlagItem,
      enabled: 0 | 1,
      value: string | null
    ): string {
      if (flag.control === "modes") {
        const mode =
          (flag.modes || []).find(
            m =>
              m.enabled === enabled &&
              (m.key === "rawV2"
                ? ["rawv2", "raw_v2"].includes(
                    String(value || "").toLowerCase()
                  ) || m.value === value
                : m.value === value)
          ) || (flag.modes || []).find(m => m.key === flag.modeKey);
        if (mode) return `${mode.key} — ${mode.description}`;
      }
      if (flag.control === "date") {
        return value ? `date ${value}` : "cleared date";
      }
      return enabled === 1
        ? `ON — ${flag.impactOn || ""}`
        : `OFF — ${flag.impactOff || ""}`;
    },
    impactForNext(
      flag: FeatureFlagItem,
      enabled: 0 | 1,
      value: string | null
    ): string {
      if (flag.control === "modes") {
        const mode = (flag.modes || []).find(
          m =>
            m.enabled === enabled &&
            (m.value === value ||
              (m.key === "rawV2" &&
                ["rawv2", "raw_v2"].includes(
                  String(value || "").toLowerCase()
                )))
        );
        return mode?.description || "";
      }
      if (flag.control === "date") {
        return value
          ? `Enforcement uses UTC date ${value}.`
          : "Date cleared — enforcement may become immediate when management is on.";
      }
      return enabled === 1 ? flag.impactOn || "" : flag.impactOff || "";
    },
    isHazardousChange(
      flag: FeatureFlagItem,
      nextEnabled: 0 | 1,
      nextValue: string | null
    ): boolean {
      if (!flag.hazard || flag.hazard.level !== "hazardous") return false;
      const direction = flag.hazard.direction || "any";
      if (direction === "any") return true;
      if (direction === "on") {
        if (flag.control === "modes") {
          return (
            nextEnabled === 1 &&
            String(nextValue)
              .toLowerCase()
              .replace("_", "") === "rawv2"
          );
        }
        return nextEnabled === 1 && flag.enabled !== 1;
      }
      if (direction === "off") {
        return nextEnabled === 0 && flag.enabled !== 0;
      }
      return false;
    },
    async promptFlip(
      flag: FeatureFlagItem,
      payload: { enabled?: 0 | 1; value?: string | null },
      nextEnabled: 0 | 1,
      nextValue: string | null
    ): Promise<boolean> {
      const currentEnabled = (flag.enabled ?? 0) as 0 | 1;
      const currentValue = flag.value;
      const hazardous = this.isHazardousChange(flag, nextEnabled, nextValue);
      return new Promise(resolve => {
        this.confirmDialog = {
          show: true,
          title: `${flag.label}: set to ${this.formatState(
            nextEnabled,
            nextValue,
            flag
          )}?`,
          currentLabel: this.stateLabel(flag, currentEnabled, currentValue),
          nextLabel: this.stateLabel(flag, nextEnabled, nextValue),
          impact: this.impactForNext(flag, nextEnabled, nextValue),
          hazardous,
          blastStatement: flag.hazard?.blastStatement || "",
          flipInput: "",
          note: "",
          flag,
          payload,
          resolve
        };
      });
    },
    resolveConfirm(ok: boolean) {
      const r = this.confirmDialog.resolve;
      const flag = this.confirmDialog.flag;
      const payload = this.confirmDialog.payload;
      const note = (this.confirmDialog.note || "").slice(0, 250);
      const hazardous = this.confirmDialog.hazardous;
      this.confirmDialog.show = false;
      this.confirmDialog.resolve = null;
      if (!ok || !flag || !payload) {
        if (r) r(false);
        return;
      }
      const body: {
        enabled?: 0 | 1;
        value?: string | null;
        note?: string;
        confirmation?: string;
      } = { ...payload, note };
      if (hazardous) body.confirmation = "FLIP";
      this.applyFlip(flag, body)
        .then(() => {
          if (r) r(true);
        })
        .catch(() => {
          if (r) r(false);
        });
    },
    async applyFlip(
      flag: FeatureFlagItem,
      body: {
        enabled?: 0 | 1;
        value?: string | null;
        note?: string;
        confirmation?: string;
      }
    ) {
      this.savingKey = flag.key;
      try {
        const updated = await updateFeatureFlag(flag.key, body);
        const idx = this.flags.findIndex(f => f.key === flag.key);
        if (idx >= 0) {
          this.$set(this.flags, idx, updated);
        }
        this.setControlDraftFromFlag(updated);
        const state = this.formatState(updated.enabled, updated.value, updated);
        this.snackbar = {
          visible: true,
          color: "success",
          text: `${updated.label} is now ${state}`
        };
      } catch (err) {
        this.snackbar = {
          visible: true,
          color: "error",
          text: err instanceof Error ? err.message : "Update failed"
        };
        throw err;
      } finally {
        this.savingKey = "";
      }
    },
    async onSwitchChange(flag: FeatureFlagItem, on: boolean) {
      if (!this.canEdit(flag)) return;
      const nextEnabled = on ? 1 : 0;
      if (flag.enabled === nextEnabled) {
        this.setControlDraftFromFlag(flag);
        return;
      }
      this.$set(this.switchDrafts, flag.key, on);
      const ok = await this.promptFlip(
        flag,
        { enabled: nextEnabled as 0 | 1 },
        nextEnabled as 0 | 1,
        flag.value
      );
      if (!ok) this.setControlDraftFromFlag(flag);
    },
    async onModeSelect(flag: FeatureFlagItem, mode: FeatureFlagMode) {
      if (!this.canEdit(flag)) return;
      if (flag.modeKey === mode.key) {
        this.setControlDraftFromFlag(flag);
        return;
      }
      this.$set(this.modeDrafts, flag.key, mode.key);
      const ok = await this.promptFlip(
        flag,
        { enabled: mode.enabled, value: mode.value },
        mode.enabled,
        mode.value
      );
      if (!ok) this.setControlDraftFromFlag(flag);
    },
    async onDateChange(flag: FeatureFlagItem, value: string) {
      if (!this.canEdit(flag)) return;
      const next = value || null;
      if (next === flag.value) return;
      this.setDateDraft(flag, next);
      const ok = await this.promptFlip(
        flag,
        { enabled: (flag.enabled ?? 1) as 0 | 1, value: next },
        (flag.enabled ?? 1) as 0 | 1,
        next
      );
      if (!ok) this.setDateDraft(flag, flag.value);
    },
    async onDateClear(flag: FeatureFlagItem) {
      if (!this.canEdit(flag) || !flag.value) return;
      this.setDateDraft(flag, null);
      const ok = await this.promptFlip(
        flag,
        { enabled: (flag.enabled ?? 1) as 0 | 1, value: null },
        (flag.enabled ?? 1) as 0 | 1,
        null
      );
      if (!ok) this.setDateDraft(flag, flag.value);
    },
    async openHistory(flag: FeatureFlagItem) {
      const requestId = ++this.historyRequestId;
      this.historyDialog = {
        show: true,
        key: flag.key,
        label: flag.label,
        loading: true,
        error: "",
        entries: []
      };
      try {
        const entries = await getFeatureFlagHistory(flag.key);
        if (
          requestId !== this.historyRequestId ||
          this.historyDialog.key !== flag.key
        ) {
          return;
        }
        this.historyDialog.entries = entries;
      } catch (err) {
        if (
          requestId !== this.historyRequestId ||
          this.historyDialog.key !== flag.key
        ) {
          return;
        }
        const message =
          err instanceof Error ? err.message : "Unable to load history";
        this.historyDialog.error = message;
        this.snackbar = {
          visible: true,
          color: "error",
          text: message
        };
      } finally {
        if (
          requestId === this.historyRequestId &&
          this.historyDialog.key === flag.key
        ) {
          this.historyDialog.loading = false;
        }
      }
    }
  }
});
